<script setup lang="ts">
import { ref, computed } from "vue";
import { TrashIcon } from "@heroicons/vue/outline";
import KekkleCommentsButton from "@/components/ui/KekkleCommentsButton.vue";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import router from "@/router/router";
import { useI18n } from "vue-i18n";
import { setErrorToast } from "@/helpers/notifications/toast";
import { formatMemberNamesList } from "@/helpers/utils/format-member-names";
import { timeAgo } from "@/helpers/dates/time-ago";
import { dateAgo } from "@/helpers/dates/date-ago";
import DilemmaOptionVoteButton from "./DilemmaOptionVoteButton.vue";
import DilemmaOptionResultBar from "./DilemmaOptionResultBar.vue";
import DilemmaOtherInput from "./DilemmaOtherInput.vue";
import InlineDeleteConfirmation from "@/components/InlineDeleteConfirmation.vue";
import type { DilemmaDocument } from "@kekkle/shared/types/documents/dilemma-document/dilemma-document";

type Props = {
  dilemma: DilemmaDocument;
  simple?: boolean;
};

const { dilemma, simple = false } = defineProps<Props>();

const dilemmasStore = useDilemmasStore();
const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const userStore = useUserStore();
const { t } = useI18n();
const isVoting = ref(false);
const deleteModalOpen = ref(false);
const isDeleting = ref(false);

function openComments(): void {
  const targetGroup = userStore.activeGroup || (dilemma as any).groupId;
  router.push({
    name: "DilemmaComments",
    params: {
      groupId: targetGroup,
      dilemmaId: dilemma._id,
    },
  });
}

const authorName = computed(() => {
  if (!dilemma.created_by) return "";
  const members = groupsStore.groupData?.members || {};
  return formatMemberNamesList(
    [dilemma.created_by],
    members,
    t("global.deleted_user"),
  );
});

const formattedTimeAgo = computed(() => {
  return dilemma.created_at ? timeAgo(dilemma.created_at) : "";
});

const formattedDateAgo = computed(() => {
  return dilemma.created_at ? dateAgo(dilemma.created_at) : "";
});

const allOptions = computed(() => {
  const options = new Set(dilemma.options);

  if (dilemma.anonymous_results) {
    Object.keys(dilemma.anonymous_results).forEach((key) => options.add(key));
  }

  if (dilemma.answers) {
    Object.values(dilemma.answers).forEach((val) => options.add(val));
  }

  return Array.from(options);
});

function hasVoted(targetDilemma: DilemmaDocument): boolean {
  return (
    targetDilemma.participants?.includes(authStore.authUuid as string) ?? false
  );
}

function canInteractWithResultBar(targetDilemma: DilemmaDocument): boolean {
  return (
    !hasVoted(targetDilemma) ||
    (targetDilemma.allow_revoting &&
      targetDilemma.anonymity === "NON_ANONYMOUS")
  );
}

function getMyVote(targetDilemma: DilemmaDocument): string | null {
  if (targetDilemma.anonymity === "ANONYMOUS") return null;
  return (
    (authStore.authUuid && targetDilemma.answers?.[authStore.authUuid]) || null
  );
}

function getVotersForOption(
  targetDilemma: DilemmaDocument,
  option: string,
): string[] {
  if (targetDilemma.anonymity === "ANONYMOUS") return [];
  const voters: string[] = [];
  for (const [uuid, answer] of Object.entries(targetDilemma.answers || {})) {
    if (answer === option) {
      voters.push(uuid);
    }
  }
  return voters;
}

function getPercentage(targetDilemma: DilemmaDocument, option: string): number {
  if (!targetDilemma.participants || targetDilemma.participants.length === 0)
    return 0;

  const count =
    targetDilemma.anonymity === "ANONYMOUS"
      ? targetDilemma.anonymous_results?.[option] || 0
      : Object.values(targetDilemma.answers || {}).filter(
          (ans) => ans === option,
        ).length;

  return Math.round((count / targetDilemma.participants.length) * 100);
}

async function castVote(
  targetDilemma: DilemmaDocument,
  option: string,
): Promise<void> {
  try {
    isVoting.value = true;
    await dilemmasStore.voteOnDilemma(
      targetDilemma._id,
      option,
      targetDilemma.anonymity === "ANONYMOUS",
    );
  } catch (e) {
    setErrorToast({
      message: t("pages.dilemmas.vote_failed", "Failed to submit vote."),
    });
  } finally {
    isVoting.value = false;
  }
}

async function handleResultBarClick(
  targetDilemma: DilemmaDocument,
  option: string,
): Promise<void> {
  if (isVoting.value || isDeleting.value) return;
  if (!canInteractWithResultBar(targetDilemma)) return;

  if (!hasVoted(targetDilemma)) {
    await castVote(targetDilemma, option);
  } else {
    if (getMyVote(targetDilemma) === option) return;
    try {
      isVoting.value = true;
      await dilemmasStore.revoteOnDilemma(targetDilemma._id, option);
    } catch (e) {
      setErrorToast({
        message: t("pages.dilemmas.vote_failed", "Failed to submit vote."),
      });
    } finally {
      isVoting.value = false;
    }
  }
}

async function executeDelete(): Promise<void> {
  isDeleting.value = true;
  try {
    await dilemmasStore.deleteDilemma(dilemma._id);
    deleteModalOpen.value = false;
  } catch (error) {
    console.error("Failed to delete dilemma:", error);
    setErrorToast({
      message: t("pages.dilemmas.delete_failed", "Failed to delete dilemma"),
    });
  } finally {
    isDeleting.value = false;
  }
}
</script>

<template>
  <div
    :id="'dilemma-' + dilemma._id"
    class="rounded-xl border-2 border-kekkle-dark-dark bg-white p-4 shadow-purple-hard-sm dark:bg-kekkle-dark-light"
  >
    <div class="mb-3 flex items-start justify-between gap-2">
      <div
        class="flex-1"
        :class="{ 'cursor-pointer': !simple }"
        @click="!simple && openComments()"
      >
        <h2
          class="text-lg font-bold text-kekkle-dark dark:text-slate-200"
          :class="{ 'transition-colors hover:text-kekkle-purple': !simple }"
        >
          {{ dilemma.question }}
        </h2>
        <p
          v-if="dilemma.description"
          class="mt-1 mb-2 text-sm text-gray-700 dark:text-gray-300"
        >
          {{ dilemma.description }}
        </p>
        <div
          class="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
        >
          <span v-if="dilemma.anonymity === 'ANONYMOUS'"
            >👻 {{ $t("pages.dilemmas.anonymous", "Anonymous voting") }}</span
          >
          <span v-else
            >👁️ {{ $t("pages.dilemmas.public", "Public voting") }}</span
          >
          <span v-if="dilemma.allow_revoting"
            >🔄 {{ $t("pages.dilemmas.can_revote", "Can change vote") }}</span
          >
          <span v-else
            >🔒 {{ $t("pages.dilemmas.cannot_revote", "Final vote") }}</span
          >
          <span
            v-if="
              dilemma.visibility === 'INSTANT' ||
              (dilemma.visibility as any) === 'ALWAYS'
            "
            >📊
            {{
              $t("pages.dilemmas.results_always_visible", "Results visible")
            }}</span
          >
          <span v-else
            >🙈
            {{
              $t(
                "pages.dilemmas.results_hidden_until_vote",
                "Vote to see results",
              )
            }}</span
          >
        </div>
      </div>

      <button
        v-if="!simple && dilemma.created_by === authStore.authUuid"
        type="button"
        class="p-1 text-red-500 hover:text-red-700"
        @click.stop="deleteModalOpen = true"
      >
        <TrashIcon class="h-5 w-5" />
      </button>
    </div>

    <!-- Voting / Results Section -->
    <div class="flex flex-col gap-2">
      <template v-for="option in allOptions" :key="option">
        <!-- Has Voted OR Visibility is INSTANT (and user can see results) -->
        <DilemmaOptionResultBar
          v-if="
            hasVoted(dilemma) ||
            ((dilemma.visibility === 'INSTANT' ||
              (dilemma.visibility as any) === 'ALWAYS') &&
              !hasVoted(dilemma))
          "
          :option-text="option"
          :percentage="getPercentage(dilemma, option)"
          :is-my-vote="getMyVote(dilemma) === option"
          :voters="getVotersForOption(dilemma, option)"
          :class="{
            'cursor-pointer transition-opacity hover:opacity-80':
              canInteractWithResultBar(dilemma),
          }"
          @click="handleResultBarClick(dilemma, option)"
        />

        <!-- Has NOT Voted -->
        <DilemmaOptionVoteButton
          v-else
          :option-text="option"
          :disabled="isVoting"
          @vote="castVote(dilemma, option)"
        />
      </template>

      <!-- Custom Other Input -->
      <DilemmaOtherInput
        v-if="dilemma.allow_other && canInteractWithResultBar(dilemma)"
        :disabled="isVoting"
        @vote="(val) => handleResultBarClick(dilemma, val)"
      />
    </div>

    <!-- Inline Delete Confirmation -->
    <InlineDeleteConfirmation
      v-if="deleteModalOpen"
      class="mt-4"
      color="red"
      :confirm-text="$t('pages.dilemmas.delete_title', 'Delete Dilemma?')"
      :cancel-text="$t('global.inputs.labels.cancel', 'Cancel')"
      :delete-text="$t('global.inputs.labels.delete', 'Delete')"
      :loading="isDeleting"
      @cancel="deleteModalOpen = false"
      @confirm="executeDelete"
    />

    <!-- Footer info & Comments row -->
    <div
      v-else
      class="mt-3 border-t border-gray-200 pt-3 dark:border-kekkle-dark-dark"
    >
      <div class="flex items-center justify-between gap-2">
        <div>
          <p class="text-xs text-gray-600 italic dark:text-gray-400">
            {{ $t("pages.dilemmas.made_by", "Made by") }}
            <span class="font-semibold">{{ authorName }}</span>
          </p>
          <p class="text-[11px] text-gray-500 italic dark:text-gray-500">
            {{ formattedTimeAgo }}
            <span v-if="formattedTimeAgo && formattedDateAgo">•</span>
            {{ formattedDateAgo }}
          </p>
        </div>
        <p class="text-xs font-bold text-kekkle-dark dark:text-slate-200">
          {{ dilemma.participants?.length || 0 }}
          {{ $t("pages.dilemmas.votes", dilemma.participants?.length || 0) }}
        </p>
      </div>

      <!-- Dedicated Comments Button Row below -->
      <div v-if="!simple" class="mt-2.5 flex items-center justify-end">
        <KekkleCommentsButton
          class="w-full sm:w-auto sm:px-4"
          :count="dilemma.number_of_comments"
          @click.stop="openComments"
        />
      </div>
    </div>
  </div>
</template>
